import axios from 'axios';
import config from '../config';

const API_URL = config.API_URL;

// Token storage keys
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';
const TOKEN_EXPIRY_KEY = 'tokenExpiry';

console.log('API URL being used:', API_URL);

// Track refresh promise to prevent multiple simultaneous refresh attempts
let refreshPromise = null;

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add interceptor to include the auth token in requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    console.log('Request headers:', config.headers);
    console.log('Request URL:', config.baseURL + config.url);
    return config;
  },
  (error) => {
    console.error('Request interception error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor to handle token expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 Unauthorized and we haven't tried to refresh already
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // If there's already a refresh in progress, wait for it
        if (refreshPromise) {
          console.log('Auth Service - Refresh already in progress, waiting...');
          await refreshPromise;
          
          // Get the new token and retry
          const newToken = localStorage.getItem(TOKEN_KEY);
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return api(originalRequest);
          }
        }
        
        // Start a new refresh
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (refreshToken) {
          console.log('Auth Service - Starting token refresh...');
          
          // Create and store the refresh promise
          refreshPromise = axios.post(`${API_URL}/auth/refresh-token`, {
            refreshToken
          }, {
            headers: { 'Content-Type': 'application/json' }
          }).finally(() => {
            refreshPromise = null;
          });
          
          const response = await refreshPromise;
          
          if (response.data.token) {
            // Store the new tokens WITHOUT clearing session data (isRefresh=true)
            storeAuthData(response.data, false, true);
            
            // Update header for the original request and retry
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
            originalRequest.headers['Authorization'] = `Bearer ${response.data.token}`;
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        refreshPromise = null;
        // Logout on refresh failure
        authService.logout();
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper to store authentication data
const storeAuthData = (data, keepLoggedIn = false, isRefresh = false) => {
  // 🚨 CRITICAL SECURITY FIX: Only clear ALL session data for new logins, not token refreshes
  if (!isRefresh) {
    console.log('🔒 SECURITY: Clearing all previous session data before storing new auth data');
    
    // 🔧 ONBOARDING FIX: Preserve onboarding data during login
    const onboardingData = {
      identityData: localStorage.getItem('identityData'),
      familyStatus: localStorage.getItem('familyStatus'),
      anniversaryDate: localStorage.getItem('anniversaryDate'),
      children: localStorage.getItem('children'),
      yartzheits: localStorage.getItem('yartzheits')
    };
    
    // Clear all localStorage data that could cause session mixing
    const allKeys = Object.keys(localStorage);
    allKeys.forEach(key => {
      // Keep only essential browser data and auth tokens, clear everything else
      if (!key.startsWith('_') && 
          key !== 'theme' && 
          key !== 'language' && 
          key !== TOKEN_KEY && 
          key !== REFRESH_TOKEN_KEY && 
          key !== USER_KEY && 
          key !== TOKEN_EXPIRY_KEY &&
          key !== 'keepLoggedIn' &&
          key !== 'redirectAfterAuth') {  // Preserve redirect URL for DirectLinkAuth
        localStorage.removeItem(key);
        console.log(`🔒 SECURITY: Cleared localStorage key: ${key}`);
      }
    });
    
    // 🔧 ONBOARDING FIX: Restore onboarding data after clearing
    Object.keys(onboardingData).forEach(key => {
      if (onboardingData[key] !== null) {
        localStorage.setItem(key, onboardingData[key]);
        console.log(`🔧 ONBOARDING FIX: Restored onboarding data: ${key}`);
      }
    });
  } else {
    console.log('🔄 TOKEN REFRESH: Updating tokens without clearing session data');
  }
  
  if (data.token) {
    localStorage.setItem(TOKEN_KEY, data.token);
    
    // Store refresh token if available
    if (data.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    }
    
    // Set token expiry based on server response
    // The server now properly handles keepLoggedIn and sends the correct expiresIn value
    const expiresIn = data.expiresIn || 3600; // Use server-provided expiry or default to 1 hour
    const expiryTime = Date.now() + expiresIn * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
    
    // Store the keepLoggedIn preference for future reference (only for new logins)
    if (!isRefresh) {
      if (keepLoggedIn) {
        localStorage.setItem('keepLoggedIn', 'true');
      } else {
        localStorage.removeItem('keepLoggedIn');
      }
    }
    
    // Store user data - this should be the ONLY user data in localStorage now
    if (data.user) {
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      console.log('🔒 SECURITY: Stored new user data:', data.user.email);
    }
  }
};

// Authentication services
const authService = {
  // Register a new user (member by default)
  register: async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      storeAuthData(response.data);
      return response.data;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  },

  // Register as admin
  registerAdmin: async (userData) => {
    try {
      const adminData = { ...userData, role: 'admin' };
      const response = await api.post('/auth/register', adminData);
      storeAuthData(response.data);
      return response.data;
    } catch (error) {
      console.error('Admin registration error:', error);
      throw error;
    }
  },

  // Login user
  login: async (credentials) => {
    try {
      const { keepLoggedIn, ...loginData } = credentials;
      const response = await api.post('/auth/login', loginData);
      
      // Store auth data for successful login with keepLoggedIn preference
      storeAuthData(response.data, keepLoggedIn);
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  // Logout user
  logout: async () => {
    try {
      // 🚨 CRITICAL SECURITY FIX: Call server logout endpoint to clear server-side cache
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        console.log('🔒 SECURITY: Calling server logout endpoint to clear server-side cache');
        try {
          await api.post('/auth/logout');
          console.log('🔒 SECURITY: Server-side cache cleared successfully');
        } catch (serverError) {
          console.error('🔒 SECURITY: Server logout failed (but continuing with client logout):', serverError);
          // Continue with client-side logout even if server call fails
        }
      }
    } catch (error) {
      console.error('🔒 SECURITY: Error during server logout, continuing with client logout:', error);
    }
    
    // Clear authentication tokens
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(TOKEN_EXPIRY_KEY);
    
    // 🚨 SECURITY FIX: Clear ALL cached data to prevent admin session mixing
    localStorage.removeItem('keepLoggedIn');
    localStorage.removeItem('pendingGroupInvite');
    localStorage.removeItem('familyStatus');
    localStorage.removeItem('anniversaryDate');
    localStorage.removeItem('children');
    localStorage.removeItem('yartzheits');
    localStorage.removeItem('identityData');
    
    // 🚨 CRITICAL FIX: Clear activeGroupId to prevent admin session mixing
    localStorage.removeItem('activeGroupId');
    localStorage.removeItem('activeAdminGroupId');
    
    // Clear any React component state cache keys that might persist
    // Check for any keys that start with common cache prefixes
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('admin_') ||
        key.startsWith('group_') ||
        key.startsWith('user_') ||
        key.startsWith('cache_') ||
        key.includes('Group') ||
        key.includes('Admin')
      )) {
        keysToRemove.push(key);
      }
    }
    
    // Remove all cache-related keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log(`🔒 SECURITY: Cleared cached data: ${key}`);
    });
    
    // Clear axios default headers
    delete api.defaults.headers.common['Authorization'];
    
    console.log('🔒 SECURITY: Complete logout - all cached data cleared (client + server)');
  },

  // Get current user
  getCurrentUser: () => {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  },

  // Check if user is authenticated
  isAuthenticated: () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    
    // Check if we have an expiry time and it's in the future
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (expiryTime) {
      return parseInt(expiryTime, 10) > Date.now();
    }
    
    // If no expiry time is stored, default to checking just token presence
    return true;
  },
  
  // Check if token needs refreshing soon (within 5 minutes)
  shouldRefreshToken: () => {
    const expiryTime = localStorage.getItem(TOKEN_EXPIRY_KEY);
    if (!expiryTime) return false;
    
    // Check if token expires within 5 minutes
    const fiveMinutesFromNow = Date.now() + 5 * 60 * 1000;
    return parseInt(expiryTime, 10) < fiveMinutesFromNow;
  },
  
  // Get user profile
  getProfile: async () => {
    try {
      return await api.get('/auth/me');
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  },
  
  // Update user's onboarding status
  updateOnboardingStatus: async (completed = true) => {
    try {
      console.log(`Attempting to update onboarding status to: ${completed}`);
      
      // Check if token exists before making the request
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Cannot update onboarding status: No authentication token found');
        throw new Error('Authentication token missing. Please log in again.');
      }
      
      // Make the API request with detailed logging
      console.log('Making request to /auth/update-onboarding endpoint');
      const response = await api.post('/auth/update-onboarding', { 
        onboardingCompleted: completed 
      });
      
      console.log('Onboarding status update response:', response.data);
      
      // Update the local user data with the new onboarding status
      const user = authService.getCurrentUser();
      if (user) {
        user.onboarding_completed = completed;
        localStorage.setItem('user', JSON.stringify(user));
        console.log('Updated local user data with new onboarding status');
      } else {
        console.warn('Could not update local user data - user not found in localStorage');
      }
      
      return response.data;
    } catch (error) {
      console.error('Onboarding status update error:', error);
      console.error('Error details:', error.response?.data || 'No detailed error information');
      console.error('Request that failed:', {
        url: '/auth/update-onboarding',
        data: { onboardingCompleted: completed }
      });
      
      throw error;
    }
  },
  
  // Check if user has completed onboarding
  hasCompletedOnboarding: () => {
    const user = authService.getCurrentUser();
    return user && user.onboarding_completed === true;
  },
  
  // Request password reset email
  requestPasswordReset: async (email) => {
    try {
      const response = await api.post('/auth/reset-password', { email });
      return response.data;
    } catch (error) {
      console.error('Password reset request error:', error);
      throw error;
    }
  },
  
  // Confirm password reset with token and new password
  confirmPasswordReset: async (token, password) => {
    try {
      console.log('Confirming password reset with token length:', token?.length);
      const response = await api.post('/auth/reset-password/confirm', { token, password });
      return response.data;
    } catch (error) {
      console.error('Password reset confirmation error:', error);
      throw error;
    }
  },
  
  // Update user password
  updatePassword: async (passwordData) => {
    try {
      const response = await api.post('/auth/update-password', passwordData);
      return response.data;
    } catch (error) {
      console.error('Password update error:', error);
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (profileData) => {
    try {
      const response = await api.post('/auth/update-profile', profileData);
      
      // Update stored user data if successful
      if (response.data.user) {
        storeAuthData({ user: response.data.user }, false, true);
      }
      
      return response.data;
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  },
  
  // Refresh the token if needed
  refreshToken: async () => {
    try {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await api.post('/auth/refresh-token', { refreshToken });
      storeAuthData(response.data, false, true); // Pass isRefresh=true to prevent clearing session data
      return response.data;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  },

  // Validate current token with server
  validateToken: async () => {
    try {
      const response = await api.get('/auth/validate-token');
      return response.data;
    } catch (error) {
      console.error('Token validation error:', error);
      throw error;
    }
  },

  // Refresh user profile data from server
  refreshUserProfile: async () => {
    try {
      const response = await api.get('/users/profile');
      if (response.data && response.data.data) {
        // Update the stored user data with fresh data from server
        localStorage.setItem(USER_KEY, JSON.stringify(response.data.data));
        console.log('User profile refreshed successfully');
        return response.data.data;
      }
      throw new Error('Invalid profile data received');
    } catch (error) {
      console.error('User profile refresh error:', error);
      throw error;
    }
  },

  // Mobile authentication methods
  mobileLoginRequest: async (phone) => {
    try {
      const response = await api.post('/auth/mobile-login-request', { phone });
      return response.data;
    } catch (error) {
      console.error('Mobile login request error:', error);
      throw error;
    }
  },

  mobileLoginVerify: async (phone, code, userId) => {
    try {
      const response = await api.post('/auth/mobile-login-verify', { phone, code, userId });
      
      if (response.data.success) {
        // Store auth data using the same method as regular login
        storeAuthData(response.data, false, false);
        console.log('📱 Mobile login successful - auth data stored via authService');
      }
      
      return response.data;
    } catch (error) {
      console.error('Mobile login verify error:', error);
      throw error;
    }
  }
};

export default authService;